import type {
	SmartField_Api_Contracts_Site_SiteDto as SiteDto,
	SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind as ObservationKind,
} from '../../../api';

type ExportFilterPanelProps = {
	sites: SiteDto[];
	selectedSiteSerialNumber: string | null;
	onSiteChange: (serialNumber: string | null) => void;
	startDate: string;
	endDate: string;
	onStartDateChange: (date: string) => void;
	onEndDateChange: (date: string) => void;
	dataTypes: Set<ObservationKind>;
	onDataTypeChange: (type: ObservationKind, checked: boolean) => void;
	measureGroups: string[];
	excludedMeasureGroups: Set<string>;
	onMeasureGroupToggle: (group: string, checked: boolean) => void;
	canReadRaw: boolean;
	canReadFlux: boolean;
};

const rawKinds = new Set<ObservationKind>(['Manual', 'Sensor']);
const kindLabels: Record<string, string> = { ChamberFlux: 'Chamber Flux', GradientFlux: 'Tower Flux' };

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent text-sm bg-white text-gray-900';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

export function ExportFilterPanel({
	sites,
	selectedSiteSerialNumber,
	onSiteChange,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	dataTypes,
	onDataTypeChange,
	measureGroups,
	excludedMeasureGroups,
	onMeasureGroupToggle,
	canReadRaw,
	canReadFlux,
}: ExportFilterPanelProps) {
	return (
		<div className="bg-white rounded-lg shadow p-4 space-y-5">
			<h3 className="font-semibold text-gray-900 text-sm">Filters</h3>

			<div>
				<p className={labelClass}>Date range</p>
				<div className="grid grid-cols-2 gap-2">
					<div>
						<label className="block text-xs text-gray-500 mb-0.5">From</label>
						<input
							type="date"
							value={startDate}
							onChange={e => onStartDateChange(e.target.value)}
							className={inputClass}
						/>
					</div>
					<div>
						<label className="block text-xs text-gray-500 mb-0.5">To</label>
						<input
							type="date"
							value={endDate}
							onChange={e => onEndDateChange(e.target.value)}
							className={inputClass}
						/>
					</div>
				</div>
			</div>

			<div>
				<p className={labelClass}>Data types</p>
				<div className="flex flex-col gap-2">
					{(['Manual', 'ChamberFlux', 'GradientFlux', 'Sensor'] as const).map(type => {
						const notQaReady = type === 'GradientFlux';
						const allowed = (rawKinds.has(type) ? canReadRaw : canReadFlux) && !notQaReady;
						const title = notQaReady
							? 'Tower flux export is temporarily disabled — not quality assured yet'
							: (allowed ? undefined : 'You do not have permission to export this data type for this site');
						const showMeasureGroups = type === 'Manual' && allowed && dataTypes.has(type) && measureGroups.length > 0;
						return (
							<div key={type}>
								<label
									className={`flex items-center gap-2 ${allowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
									title={title}
								>
									<input
										type="checkbox"
										checked={dataTypes.has(type)}
										disabled={!allowed}
										onChange={e => onDataTypeChange(type, e.target.checked)}
										className="accent-smartfield w-4 h-4 flex-shrink-0"
									/>
									<span className="text-sm text-gray-700">{kindLabels[type] ?? type}</span>
								</label>
								{showMeasureGroups && (
									<fieldset className="ml-6 mt-1 flex flex-col gap-1 max-h-48 overflow-y-auto">
										<legend className="sr-only">Manual measure groups</legend>
										{measureGroups.map(group => (
											<label key={group} className="flex items-center gap-2 cursor-pointer">
												<input
													type="checkbox"
													checked={!excludedMeasureGroups.has(group)}
													onChange={e => onMeasureGroupToggle(group, e.target.checked)}
													className="accent-smartfield w-3.5 h-3.5 flex-shrink-0"
												/>
												<span className="text-xs text-gray-600">{group}</span>
											</label>
										))}
									</fieldset>
								)}
							</div>
						);
					})}
				</div>
			</div>

			<div>
				<label className={labelClass}>Site</label>
				<select
					value={selectedSiteSerialNumber ?? ''}
					onChange={e => onSiteChange(e.target.value === '' ? null : e.target.value)}
					className={inputClass}
				>
					<option value="">All Sites</option>
					{sites.map(site => (
						<option key={site.id} value={site.serialNumber}>
							{site.name} ({site.serialNumber})
						</option>
					))}
				</select>
			</div>

		</div>
	);
}
